import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Spin,
  Select,
  DatePicker,
  Tabs,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import useLatestRequest from "../utils/useLatestRequest";
import {
  REPORT_DATE_FORMAT,
  formatReportDate,
} from "../utils/reportDateRangeUtils";

const { Option } = Select;
const { RangePicker } = DatePicker;

const formatNumber = (v) =>
  typeof v === "number" ? new Intl.NumberFormat("en-IN").format(v) : (v ?? "-");

const BDFNotSentToSalesOrderReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [dateRange, setDateRange] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("bdf");
  const [bdfRows, setBdfRows] = useState([]);
  const [subBdfRows, setSubBdfRows] = useState([]);

  const beginRequest = useLatestRequest();

  const fetchData = useCallback(
    async (isExcel = false) => {
      const isCurrent = isExcel ? () => true : beginRequest();
      if (isExcel) setDownloading(true);
      else {
        setLoading(true);
        setBdfRows([]);
        setSubBdfRows([]);
      }
      try {
        const params = new URLSearchParams();
        const startDate = formatReportDate(dateRange?.[0]);
        const endDate = formatReportDate(dateRange?.[1]);
        if (startDate && endDate) {
          params.set("startDate", startDate);
          params.set("endDate", endDate);
        } else {
          params.set("financialYear", financialYear);
        }
        if (isExcel) params.set("excel", "true");
        const q = `?${params.toString()}`;
        const url = `${apiBaseUrl}/inventoryManagement/getBDFNotSentToSalesOrderReport${q}`;

        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (isExcel) {
          if (!res.ok) {
            message.error("Failed to download Excel");
            return;
          }
          const blob = await res.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          const dateLabel =
            startDate && endDate
              ? `${startDate}_to_${endDate}`
              : financialYear;
          a.download = `BDF_Not_Sent_To_SO_Report_${dateLabel}_${moment().format(
            "YYYY-MM-DD"
          )}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
          return;
        }

        const j = await res.json();
      if (!isCurrent()) return;
        if (j.success) {
          setBdfRows(j.bdf || []);
          setSubBdfRows(j.subBdf || []);
        } else {
          console.error("getBDFNotSentToSalesOrderReport failed:", j.message);
          message.error(j.message || "Failed to fetch report");
        }
      } catch (err) {
        console.error(err);
        message.error(isExcel ? "Error downloading Excel" : "Error fetching report");
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setDownloading(false);
        }
      }
    },
    [apiBaseUrl, financialYear, dateRange]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const handleDownloadExcel = () => {
    fetchData(true);
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "stamp",
      key: "stamp",
      width: 130,
      align: "center",
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      width: 120,
      align: "center",
      render: (d) => (d ? moment(d).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Party Name",
      dataIndex: "partyName",
      key: "partyName",
      width: 200,
      align: "left",
    },
    {
      title: "Brand Name",
      dataIndex: "brandName",
      key: "brandName",
      width: 160,
      align: "left",
    },
    {
      title: "Campaign Name",
      dataIndex: "campaignName",
      key: "campaignName",
      width: 180,
      align: "left",
    },
    {
      title: "Sales Person",
      dataIndex: "salesPerson",
      key: "salesPerson",
      width: 140,
      align: "left",
    },
    {
      title: "Media Planner",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 140,
      align: "left",
    },
    {
      title: "Inventories Count",
      dataIndex: "totalInventories",
      key: "totalInventories",
      width: 130,
      align: "right",
      render: formatNumber,
    },
    {
      title: "HO Amount",
      dataIndex: "totalHO",
      key: "totalHO",
      width: 130,
      align: "right",
      render: formatNumber,
    },
    {
      title: "Production Amount",
      dataIndex: "totalProduction",
      key: "totalProduction",
      width: 140,
      align: "right",
      render: formatNumber,
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  const renderTable = (rows) => (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey={(r, i) => r.stamp?.toString() || `row-${i}`}
      pagination={false}
      scroll={{ x: "max-content", y: 500 }}
      components={components}
      sticky
    />
  );

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              BDF Not Sent to Sales Order
            </Space>

            <Space wrap>
              <Select
                value={financialYear}
                onChange={(v) => {
                  setFinancialYear(v);
                  setDateRange(null);
                }}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format={REPORT_DATE_FORMAT}
                style={{ width: 220 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : (
          <Tabs
            activeKey={activeTabKey}
            onChange={setActiveTabKey}
            items={[
              {
                key: "bdf",
                label: `BDF (${bdfRows.length})`,
                children:
                  bdfRows.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
                      No data found
                    </div>
                  ) : (
                    renderTable(bdfRows)
                  ),
              },
              {
                key: "subBdf",
                label: `Sub BDF (${subBdfRows.length})`,
                children:
                  subBdfRows.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
                      No data found
                    </div>
                  ) : (
                    renderTable(subBdfRows)
                  ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default BDFNotSentToSalesOrderReport;
